import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Button, tokens } from '@hotjar/hotsauce';
import type { TargetingOptionValue, TopPage } from '@hotjar/heatmaps';
import { TARGETING_OPTION_VALUE, TARGETING_VALUE } from '@hotjar/heatmaps';
import { UrlSearch } from '@hotjar/webapp/features/Heatmaps/ContinuousCaptureReport/SearchBar/UrlSearch';
import { useSearchBar } from '@hotjar/webapp/features/Heatmaps/hooks/useSearchBar';
import { HEATMAP_VIEWED_TRIGGERS } from '@hotjar/webapp/domains/heatmaps/tracking';
import { TargetOptionsSelector } from './TargetOptionsSelector';

const SearchBarContainer = styled.div<{ hasError: boolean }>`
  margin-bottom: ${({ hasError }) => (hasError ? '0' : tokens.spacer.x3)};
  border-radius: ${tokens.radius.m};
`;

const SearchBarGroup = styled.form`
  display: flex;
  justify-content: space-between;
  border-radius: ${tokens.radius.m};
`;

const flexedContainerAndChildren = css`
  display: flex;
  flex: 1;

  & > div {
    flex: 1;
  }
`;

const StyledUrlSearch = styled(UrlSearch)`
  ${flexedContainerAndChildren}
`;

const SearchAndSuggestionsWrapper = styled.div`
  ${flexedContainerAndChildren}
`;

const StyledButton = styled(Button)`
  margin-left: ${tokens.spacer.x1};
`;

const Error = styled.p`
  color: ${tokens.color.text_danger};
  margin: ${tokens.spacer.x0} 0;
  font-size: 12px;
`;

export interface SearchBarProps {
  sourceUrl: string;
  selectedTargetingValue: TargetingValue;
  onTargetChange: (value: TargetingValue) => void;
  onSubmit: (url: string, properties?: Partial<HeatmapAPI.TrackRequest>) => void;
  urlSuggestions?: TopPage[];
  initiallyOpen?: boolean;
  onClear: () => void;
}

export const getTargetingOptionByTargetingValue = (
  targetingValue: TargetingValue,
): TargetingOptionValue => {
  return targetingValue === TARGETING_VALUE.SIMPLE
    ? TARGETING_OPTION_VALUE.SINGLE_PAGE
    : targetingValue;
};

export const SearchBar = ({
  sourceUrl = '',
  selectedTargetingValue,
  onTargetChange,
  onSubmit,
  urlSuggestions = [],
  initiallyOpen = false,
  onClear,
}: SearchBarProps): JSX.Element => {
  const { t } = useTranslation('heatmaps');
  const [url, setUrl] = useState<SearchBarProps['sourceUrl']>(sourceUrl);
  const [targetingOption, setTargetingOption] = useState(
    getTargetingOptionByTargetingValue(selectedTargetingValue),
  );

  const {
    getTargetingValueByTargetingOption,
    getPlaceholder,
    shouldHideUrlSuggestions,
    validateSelectedUrl,
  } = useSearchBar(targetingOption);

  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    setTargetingOption(getTargetingOptionByTargetingValue(selectedTargetingValue));
  }, [selectedTargetingValue]);

  useEffect(() => {
    setUrl(sourceUrl);
  }, [sourceUrl]);

  const handleUrlChange = (newUrl: string) => {
    setErrorMessage('');
    setUrl(newUrl);
    if (!newUrl) {
      onClear();
    }
  };

  const handleSuggestionSelect = (selectedUrl: string, index: number) => {
    setUrl(selectedUrl);
    onSubmit(selectedUrl, {
      trigger: HEATMAP_VIEWED_TRIGGERS.AUTOCOMPLETE,
      autocompleteRank: index + 1,
    });
  };

  const updateTargetingValue = (value: TargetingValue) => {
    setErrorMessage('');
    onTargetChange(value);
  };

  const handleTargetChange = (selectedValue: TargetingOptionValue) => {
    setTargetingOption(selectedValue);
    updateTargetingValue(getTargetingValueByTargetingOption(selectedValue));
  };

  const handleSubmit = () => {
    if (!url) {
      return;
    }

    const error = validateSelectedUrl(url);
    if (error) {
      setErrorMessage(error);
      return;
    }

    onSubmit(url, {
      trigger: HEATMAP_VIEWED_TRIGGERS.URL_SEARCH,
    });
  };

  return (
    <SearchBarContainer hasError={!!errorMessage}>
      <SearchBarGroup onSubmit={(e) => e.preventDefault()}>
        <TargetOptionsSelector
          selectedTargetingValue={targetingOption}
          onTargetChange={handleTargetChange}
        />
        <SearchAndSuggestionsWrapper>
          <StyledUrlSearch
            initiallyOpen={initiallyOpen}
            onSuggestionSelect={handleSuggestionSelect}
            onUrlChange={handleUrlChange}
            placeholderMessage={getPlaceholder(getTargetingValueByTargetingOption(targetingOption))}
            shouldHideUrlSuggestions={shouldHideUrlSuggestions}
            sourceUrl={url}
            urlSuggestions={urlSuggestions}
          />
        </SearchAndSuggestionsWrapper>
        <StyledButton size="m" kind="secondary" onClick={handleSubmit} type="submit">
          {t('searchbar.action-search')}
        </StyledButton>
      </SearchBarGroup>
      {!!errorMessage && <Error>{errorMessage}</Error>}
    </SearchBarContainer>
  );
};
